import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { MoleculeFacts, MoleculeSummary } from '@peruselab/api-client/molecules';
import { Icon } from '../../components/Icon';
import { readableSynonyms } from '../chemistry/molecule/factsheetText';
import { LearnMore, OtherSources } from '../chemistry/molecule/PublishedFacts';
import { StatementSection } from '../chemistry/molecule/StatementForm';
import { UnusualChargeNote } from '../chemistry/molecule/UnusualChargeNote';
import { SaltLinks } from '../chemistry/molecule/SaltLinks';
import { StructureCheckLine } from '../chemistry/molecule/StructureCheckLine';
import { MonographSafety } from './MonographSafety';
import { statementsFor, type MoleculePropertiesState } from '../chemistry/molecule/useMoleculeProperties';
import { usePublicAccess } from '../public/publicAccess';
import { MonographHero } from './MonographHero';
import { MonographSmiles } from './MonographSmiles';
import { propertyCards } from './propertyCards';

interface MonographSheetProps {
  readonly summary: MoleculeSummary;
  readonly facts: MoleculeFacts | null;
  readonly name: string | null;
  readonly formula: ReactNode;
  readonly weight: string | null;
  readonly noPublished: boolean;
  readonly properties: MoleculePropertiesState;
  /** The Properties table (PropertiesSection, layout "table"); each addRequest increase opens its form. */
  readonly renderPropertiesTable: (request: { readonly addRequest: number }) => ReactNode;
  readonly discussion: ReactNode;
}

/**
 * #930 (epic #934): the Monograph as a tidy reference sheet -- direction **A · Reference sheet** on
 * the design canvas. The identity block (structure, name, identifiers), a contents rail, then About,
 * a properties table whose rows open to every value, Uses, Safety, the discussion and the sources.
 * The same data as the Cards view (#929); only the presentation differs.
 */
export function MonographSheet({
  summary,
  facts,
  name,
  formula,
  weight,
  noPublished,
  properties,
  renderPropertiesTable,
  discussion
}: MonographSheetProps) {
  const { t } = useTranslation();
  const { signedIn, signIn } = usePublicAccess();
  const location = useLocation();
  const [addRequest, setAddRequest] = useState(0);
  const addValue = () => {
    if (!signedIn) {
      signIn(location.pathname + location.search);
      return;
    }
    setAddRequest((request) => request + 1);
    document.getElementById('monograph-properties')?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };
  const titleId = useId();
  const ready = facts?.status === 'ready' ? facts : null;
  const alsoKnownAs = ready ? readableSynonyms(ready.synonyms, name) : [];
  const propertyCount = properties.loaded ? propertyCards(properties.loaded).length : 0;
  // #947: statements are assertions once imported; PubChem's cached text until then.
  const about = statementsFor(properties.loaded, 'description', ready?.about);
  const uses = statementsFor(properties.loaded, 'use', ready?.uses);
  const toxicity = statementsFor(properties.loaded, 'toxicity-summary', ready?.toxicity);
  const cid = summary.pubchemCid ? Number(summary.pubchemCid) : null;

  const contents: readonly { readonly id: string; readonly label: string }[] = [
    { id: 'monograph-identifiers', label: t('moleculePage.identifiers') },
    { id: 'monograph-about', label: t('moleculePage.about') },
    {
      id: 'monograph-properties',
      label: propertyCount > 0 ? t('monograph.tabs.propertiesCount', { count: propertyCount }) : t('properties.heading')
    },
    { id: 'monograph-uses', label: t('moleculePage.uses') },
    { id: 'monograph-safety', label: t('moleculePage.safety') },
    { id: 'monograph-discussion', label: t('monograph.sheet.discussion') },
    ...(ready ? [{ id: 'monograph-sources', label: t('monograph.tabs.sources') }] : [])
  ];

  return (
    <article className="monograph-sheet">
      <MonographHero
        summary={summary}
        facts={facts}
        about={about}
        name={name}
        formula={formula}
        weight={weight}
        onAddValue={addValue}
      />

      <div className="monograph-sheet-body">
        <nav className="monograph-sheet-contents" aria-label={t('monograph.sheet.contents')}>
          <p>{t('monograph.sheet.contents')}</p>
          <ol>
            {contents.map((entry) => (
              <li key={entry.id}>
                <a href={`#${entry.id}`}>{entry.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="monograph-sheet-sections">
          <section
            id="monograph-identifiers"
            className="monograph-sheet-identifiers-section"
            aria-labelledby={`${titleId}-identifiers`}
          >
            <h2 id={`${titleId}-identifiers`}>{t('moleculePage.identifiers')}</h2>
            <dl className="monograph-sheet-identifiers">
              {alsoKnownAs.length > 0 ? (
                <div className="monograph-sheet-wide">
                  <dt>{t('monograph.sheet.otherNames')}</dt>
                  <dd className="monograph-sheet-names" lang="en" dir="auto">
                    {alsoKnownAs.join(' · ')}
                  </dd>
                </div>
              ) : null}
              {summary.smiles ? (
                <div className="monograph-sheet-wide">
                  <dt>{t('moleculePage.smiles')}</dt>
                  <dd className="monograph-sheet-copyable">
                    <MonographSmiles smiles={summary.smiles} haptoBonds={summary.haptoBonds} />
                  </dd>
                </div>
              ) : null}
              {ready?.inchiKey ? (
                <div className="monograph-sheet-wide">
                  <dt>{t('moleculePage.inchiKey')}</dt>
                  <dd>
                    <code dir="ltr">{ready.inchiKey}</code>{' '}
                    <span className="monograph-sheet-from">{t('moleculePage.fromPubChem')}</span>
                  </dd>
                </div>
              ) : null}
              {summary.cas ? (
                <div>
                  <dt>{t('moleculePage.cas')}</dt>
                  <dd>
                    <span dir="ltr">{summary.cas}</span>{' '}
                    <span className="monograph-sheet-from">{t('moleculePage.fromPubChem')}</span>
                  </dd>
                </div>
              ) : null}
            </dl>
            {noPublished ? (
              // Principle 4: honest about gaps -- the page is still whole, and says why it's short.
              <p className="molecule-factsheet-gap">
                <Icon name="info" size="1rem" />
                <span>{t('moleculePage.noPublished')}</span>
              </p>
            ) : null}
          </section>
          <StructureCheckLine moleculeId={summary.id} />
          <SaltLinks moleculeId={summary.id} />
          <UnusualChargeNote facts={facts} />
          {about.length > 0 ? null : facts?.status === 'pending' ? (
            <p className="published-facts-status" role="status">
              {t('moleculePage.factsLoading')}
            </p>
          ) : facts?.status === 'unavailable' ? (
            <p className="published-facts-status">{t('moleculePage.factsUnavailable')}</p>
          ) : null}
          <div id="monograph-about">
            <StatementSection
              moleculeId={summary.id}
              propertyId="description"
              title={t('moleculePage.about')}
              statements={about}
              onChanged={() => void properties.reload()}
            />
          </div>
          <div id="monograph-properties">{renderPropertiesTable({ addRequest })}</div>
          <div id="monograph-uses">
            <StatementSection
              moleculeId={summary.id}
              propertyId="use"
              title={t('moleculePage.uses')}
              statements={uses}
              onChanged={() => void properties.reload()}
            />
          </div>
          <div id="monograph-safety">
            <MonographSafety
              moleculeId={summary.id}
              facts={facts}
              toxicity={toxicity}
              onChanged={() => void properties.reload()}
            />
          </div>
          <div id="monograph-discussion">{discussion}</div>
          {ready ? (
            <section id="monograph-sources" className="published-facts-section" aria-labelledby={`${titleId}-sources`}>
              <h2 id={`${titleId}-sources`}>{t('monograph.tabs.sources')}</h2>
              <OtherSources sources={ready.otherSources} />
              {cid ? <LearnMore cid={cid} /> : null}
            </section>
          ) : null}
        </div>
      </div>
    </article>
  );
}
